import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaVolumeUp, FaCheck, FaTimes } from 'react-icons/fa';
import { playSmartAudio } from '../utils/audioUtils';
import { persianQuestionsData } from '../data/persianQuestions';
import type { Lesson } from '../data/persianQuestions';

const VajakFlashcards: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [completed, setCompleted] = useState<number[]>([]);

  useEffect(() => {
    if (lessonId) {
      const found = persianQuestionsData.find(l => (typeof l.id === 'string' && l.id === lessonId) || (typeof l._id === 'string' && l._id === lessonId));
      if (found) {
        setLesson(found);
      } else {
        navigate('/vajak');
      }
    }
  }, [lessonId, navigate]);

  if (!lesson) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const questions = lesson.questions;
  const current = questions[currentIndex];
  const progress = Math.round(((currentIndex + 1) / questions.length) * 100);

  const handleNext = () => {
    setShowAnswer(false);
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(i => i + 1);
    }
  };
  const handlePrev = () => {
    setShowAnswer(false);
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
    }
  };
  const handleMark = (know: boolean) => {
    setCompleted(prev => know ? Array.from(new Set([...prev, current.id])) : prev.filter(id => id !== current.id));
    handleNext();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to={`/vajak/lesson/${lesson.id}`}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Back to Lesson
          </Link>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {lesson.title} Flashcards
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {lesson.persianTitle}
            </p>
          </div>
          <div className="w-24"></div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Progress
            </h3>
            <span className="text-lg font-bold text-orange-500">
              {progress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Card {currentIndex + 1} of {questions.length}</span>
            <span>{completed.length} known</span>
          </div>
        </div>

        {/* Flashcard */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="relative">
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-10 shadow-2xl cursor-pointer transition-all duration-500 min-h-[260px] flex flex-col items-center justify-center"
              onClick={() => setShowAnswer(a => !a)}
            >
              {/* Persian */}
              <div className="text-5xl font-farsi font-extrabold text-gray-900 dark:text-white mb-6 text-center drop-shadow-xl">
                {current.persian}
              </div>
              <div className="text-lg text-gray-600 dark:text-gray-300 mb-2">
                {current.pronunciation}
              </div>
              <button
                onClick={e => { e.stopPropagation(); playSmartAudio(undefined, current.persian); }}
                className="mt-2 mb-4 w-12 h-12 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-full flex items-center justify-center shadow-lg border-2 border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-400"
                title="Play pronunciation"
              >
                <FaVolumeUp />
              </button>
              {!showAnswer ? (
                <div className="text-base text-gray-500 dark:text-gray-400 mt-4 font-medium tracking-wide">
                  Click to see the English
                </div>
              ) : (
                <div className="mt-6 w-full">
                  <div className="text-2xl font-bold text-green-600 bg-green-50 rounded px-2 py-1 mb-2 drop-shadow-md text-center">
                    {current.english}
                  </div>
                  <div className="flex justify-center gap-4 mt-6">
                    <button
                      onClick={e => { e.stopPropagation(); handleMark(true); }}
                      className="flex items-center px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold"
                    >
                      <FaCheck className="mr-2" /> I Know This
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); handleMark(false); }}
                      className="flex items-center px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold"
                    >
                      <FaTimes className="mr-2" /> Need Practice
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between max-w-xl mx-auto">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              currentIndex === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gray-500 text-white hover:bg-gray-600'
            }`}
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === questions.length - 1}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              currentIndex === questions.length - 1
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default VajakFlashcards; 